import { onUnmounted, ref } from "vue"

/**
 * Wraps the Web Speech API for voice-to-text transcription.
 *
 * Returns a reactive transcript that updates as the user speaks.
 * Falls back gracefully when the API isn't available (most mobile
 * browsers support it; Firefox desktop does not).
 *
 * @param options.continuous - Keep listening after pauses (default true).
 * @param options.lang - BCP 47 language tag (default "en-US").
 *
 * @example
 * ```ts
 * const { start, stop, transcript, isListening, isSupported } = useSpeechRecognition()
 * start()
 * // transcript.value updates as user speaks
 * stop()
 * ```
 */
export function useSpeechRecognition(options?: { continuous?: boolean; lang?: string }) {
  const isSupported = ref(
    typeof window !== "undefined" &&
      ("SpeechRecognition" in window || "webkitSpeechRecognition" in window),
  )
  const isListening = ref(false)
  const transcript = ref("")

  let recognition: SpeechRecognition | null = null

  function createRecognition(): SpeechRecognition {
    const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition
    const r = new Ctor()
    r.continuous = options?.continuous ?? true
    r.interimResults = true
    r.lang = options?.lang ?? "en-US"

    r.addEventListener("result", ((event: SpeechRecognitionEvent) => {
      let text = ""
      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0].transcript
      }
      transcript.value = text
    }) as EventListener)

    r.addEventListener("end", () => {
      isListening.value = false
    })

    r.addEventListener("error", ((event: SpeechRecognitionErrorEvent) => {
      if (event.error !== "aborted") {
        console.warn("Speech recognition error:", event.error)
      }
      isListening.value = false
    }) as EventListener)

    return r
  }

  function start() {
    if (!isSupported.value || isListening.value) return
    transcript.value = ""
    recognition = createRecognition()
    try {
      recognition?.start()
      isListening.value = true
    } catch {
      recognition = null
    }
  }

  function stop(): string {
    recognition?.stop()
    recognition = null
    isListening.value = false
    return transcript.value
  }

  onUnmounted(() => {
    recognition?.abort()
    recognition = null
  })

  return { start, stop, transcript, isListening, isSupported }
}
